import { Box, Container, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  wholesalerEditRequest,
  wholesalerResetState,
  wholesalerViewRequest,
} from "../../../redux/wholesaler/WholesalerAction";
import {
  handleFileChange,
  convertImageToBase64,
  handleProofChangeMultiImage,
  convertProofToBase64MultiImage,
} from "../../constant/commonFileUpload";
import CloseIcon from "@mui/icons-material/Close";
import {
  convertTo24HourFormat,
  formatToAMPM,
} from "../../constant/commonTimeFormat";
import "../../../style/style.css";
import { StyledContainer, styles } from "../../components/formStyles";
import CustomButton from "../../constant/commonButton";
import ReusableFormFields from "../../constant/reusableFormFields";
import { APP_LINK } from "../../global/MenuItems";
import CommonDialog from "../../notification/Dialogbox";
import CommonSnackbar from "../../notification/Snackbar";
import uploadImage from "../../../image/wmremove-transformed-Photorooms.jpg";
import pdf from "../../../image/pdf.jpg";
import ImagePreviewDialog from "../../constant/commonImageDialog";

const EditWholesaler = () => {
  const {
    register,
    handleSubmit,
    setValue,
    control,
    setError,
    reset,
    formState: { errors },
  } = useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [imageFileName, setImageFileName] = useState("");
  const [proofFileName, setProofFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [proofSelected, setProofSelected] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [proofPreview, setProofPreview] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState([]);
  const [existingProofUrls, setExistingProofUrls] = useState([]);
  const [newProofBase64, setNewProofBase64] = useState([]);
  const [isImageOnlySave, setIsImageOnlySave] = useState(false);
  const [hasImageChanged, setHasImageChanged] = useState(false);

  // Track which images are marked for removal
  const [removedExistingIndices, setRemovedExistingIndices] = useState(
    new Set()
  );
  const [removedNewIndices, setRemovedNewIndices] = useState(new Set());

  const wholesalerState = useSelector(
    (state) => state?.wholesaler?.viewWholesaler?.data || {}
  );
  const wholesalerList = wholesalerState || [];
  const editWholesalerState = useSelector(
    (state) => state?.wholesaler?.editWholesaler
  );

  const handleCancel = () => {
    navigate(APP_LINK.ONBOARDEDWHOLESALER);
  };

  const inputFields = [
    {
      name: "reg_no",
      label: "Register No",
      readOnly: true,
    },
    {
      name: "name",
      label: "Company Name",
    },
    {
      name: "license_no",
      label: "License No",
      rules: {
        required: "License No is required",
        pattern: {
          value: /^[A-Z0-9-]{5,15}$/,
          message:
            "License No must be 5-15 characters (letters, numbers, hyphens).",
        },
      },
    },
    {
      name: "tax_id",
      label: "Tax ID",
      rules: {
        required: "Tax ID is required",
        pattern: {
          value: /^[0-9]{9,12}$/,
          message: "Tax ID must be 9-12 digits long.",
        },
      },
    },
    {
      name: "company_reg_no",
      label: "Company Reg No",
      rules: {
        required: "Company Reg No is required",
        pattern: {
          value: /^[A-Z0-9]{8,12}$/,
          message: "Company Reg No must be 8-12 uppercase letters or numbers.",
        },
      },
    },
    {
      name: "first_name",
      label: "First Name",
    },
    {
      name: "last_name",
      label: "Last Name",
    },
    {
      name: "email",
      label: "Email Address",
      rules: {
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email format",
        },
      },
    },
    {
      name: "contact",
      label: "Phone No",
      rules: {
        required: "Phone No is required",
        pattern: {
          value: /^\d{8,}$/,
          message: "Invalid Phone Number",
        },
      },
    },
    {
      name: "address",
      label: "Address",
      size: "extraLarge",
    },
    {
      name: "working_time",
      label: "Working Time",
      type: "group",
      fields: [
        {
          name: "open_time",
          label: "Start Time",
          type: "time",
        },
        {
          name: "close_time",
          label: "End Time",
          type: "time",
        },
      ],
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      options: [
        { value: "1", label: "Active" },
        { value: "2", label: "Inactive" },
      ],
      defaultValue: wholesalerList?.status,
      rules: { required: "Status is required" },
    },
  ];

  const handleImageUpload = (event) => {
    setHasImageChanged(true);
    handleFileChange(
      event,
      setSelectedFile,
      setImagePreview,
      setImageFileName,
      showSnackbar
    );
  };

  // handleProofUpload function to prevent duplicate display
  const handleProofUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const newBase64Images = [];
    const newPreviewUrls = [];
    let processedFiles = 0;

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result.split(",")[1];
        newBase64Images.push(base64);

        if (
          file.type === "application/pdf" ||
          file.name.toLowerCase().endsWith(".pdf")
        ) {
          newPreviewUrls.push(pdf);
        } else {
          newPreviewUrls.push(e.target.result);
        }

        processedFiles++;
        if (processedFiles === files.length) {
          setNewProofBase64((prev) => [...prev, ...newBase64Images]);
          setProofPreview((prev) => [...prev, ...newPreviewUrls]);
          setProofFileName(files.map((f) => f.name).join(", "));
        }
      };
      reader.readAsDataURL(file);
    });
  };

  useEffect(() => {
    if (id) {
      dispatch(wholesalerViewRequest(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    reset();
    setRemovedExistingIndices(new Set());
    setRemovedNewIndices(new Set());
    setHasImageChanged(false);
    setNewProofBase64([]);

    if (wholesalerList) {
      setValue("reg_no", wholesalerList?.reg_no);
      setValue("first_name", wholesalerList?.first_name);
      setValue("last_name", wholesalerList?.last_name);
      setValue("name", wholesalerList?.name);
      setValue("address", wholesalerList?.address);
      setValue("email", wholesalerList?.email);
      setValue("tax_id", wholesalerList?.tax_id);
      setValue("company_reg_no", wholesalerList?.company_reg_no);
      setValue("license_no", wholesalerList?.license_no);
      setValue("contact", wholesalerList?.contact);
      setValue("working_time", wholesalerList?.working_time);
      setValue("status", wholesalerList?.status);

      const imageUrl = wholesalerList?.prof_image;
      if (imageUrl) {
        if (imageUrl.startsWith("data:image")) {
          setSelectedFile(imageUrl.split(",")[1]);
          setImagePreview(imageUrl);
        } else {
          setImagePreview(imageUrl);
        }
      }

      const proofUrls = Array.isArray(wholesalerList?.govt_id_image)
        ? wholesalerList.govt_id_image.filter((img) => img !== null)
        : wholesalerList?.govt_id_image
        ? [wholesalerList.govt_id_image]
        : [];

      const validProofUrls = proofUrls.filter((url) => url !== uploadImage);
      const convertProofPromises = validProofUrls.map((url) =>
        convertProofToBase64MultiImage(url)
      );
      Promise.all(convertProofPromises).then((base64ProofImages) => {
        setExistingProofUrls(base64ProofImages);
        setProofPreview(validProofUrls);
      });
    }
  }, [wholesalerList, setValue]);

  useEffect(() => {
    if (wholesalerState?.working_time) {
      const timeParts = wholesalerState?.working_time.split(" to ");

      if (timeParts.length === 2) {
        setValue("open_time", convertTo24HourFormat(timeParts[0]), {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue("close_time", convertTo24HourFormat(timeParts[1]), {
          shouldDirty: true,
          shouldValidate: true,
        });
      } else {
        setValue("open_time", "", { shouldDirty: true, shouldValidate: true });
        setValue("close_time", "", { shouldDirty: true, shouldValidate: true });
      }
    }
  }, [wholesalerState?.working_time, setValue]);

  const onSubmit = async (data) => {
    const filteredExistingProofs = existingProofUrls.filter(
      (_, index) => !removedExistingIndices.has(index)
    );

    const filteredNewProofs = newProofBase64.filter(
      (_, index) => !removedNewIndices.has(index)
    );

    let govtImages = [...filteredExistingProofs, ...filteredNewProofs];

    const normalizeText = (text) => (text ? text.replace(/\s+/g, "") : "");

    const { govt_id_image, profile_image, bar_image, ...cleanedData } = data;

    let profileImageData = null;
    if (hasImageChanged && selectedFile) {
      profileImageData = selectedFile;
    } else if (!hasImageChanged && wholesalerList?.prof_image) {
      try {
        const base64Data = await convertImageToBase64(
          wholesalerList.prof_image
        );
        profileImageData = base64Data || wholesalerList.prof_image;
      } catch (error) {
        console.error("Failed to convert existing image:", error);
        profileImageData = wholesalerList.prof_image;
      }
    }

    const editRequestData = {
      ...cleanedData,
      working_time: `${formatToAMPM(data.open_time)} to ${formatToAMPM(
        data.close_time
      )}`,
      tax_id: normalizeText(data?.tax_id),
      company_reg_no: normalizeText(data?.company_reg_no),
      license_no: normalizeText(data?.license_no),
      prof_image: profileImageData,
      govt_img_url: govtImages,
      id,
    };

    dispatch(wholesalerEditRequest(id, editRequestData));
  };

  const handleImage = async () => {
    let profileImageData = null;

    if (hasImageChanged && selectedFile) {
      profileImageData = selectedFile;
    } else if (wholesalerList?.prof_image) {
      try {
        const base64Data = await convertImageToBase64(
          wholesalerList.prof_image
        );
        profileImageData = base64Data || wholesalerList.prof_image;
      } catch (error) {
        console.error("Failed to convert existing image:", error);
        profileImageData = wholesalerList.prof_image;
      }
    } else {
      showSnackbar("Please choose a file before saving.", "error");
      return;
    }

    const data = {
      ...wholesalerList,
      prof_image: profileImageData,
    };
    setIsImageOnlySave(true);
    onSubmit(data);
  };

  const proofImage = () => {
    const filteredExistingProofs = existingProofUrls.filter(
      (_, index) => !removedExistingIndices.has(index)
    );

    const filteredNewProofs = newProofBase64.filter(
      (_, index) => !removedNewIndices.has(index)
    );

    let govtImages = [...filteredExistingProofs, ...filteredNewProofs];

    const data = {
      ...wholesalerList,
      govt_img_url: govtImages,
    };
    setIsImageOnlySave(true);
    onSubmit(data);
  };

  // removeProofImage function with proper index mapping
  const removeProofImage = (displayIndex) => {
    const existingValidCount = existingProofUrls.filter(
      (_, index) => !removedExistingIndices.has(index)
    ).length;

    if (displayIndex < existingValidCount) {
      let actualIndex = 0;
      let validCount = 0;
      for (let i = 0; i < existingProofUrls.length; i++) {
        if (!removedExistingIndices.has(i)) {
          if (validCount === displayIndex) {
            actualIndex = i;
            break;
          }
          validCount++;
        }
      }
      setRemovedExistingIndices((prev) => new Set([...prev, actualIndex]));
    } else {
      const newImageIndex = displayIndex - existingValidCount;
      let actualNewIndex = 0;
      let validNewCount = 0;
      for (let i = 0; i < newProofBase64.length; i++) {
        if (!removedNewIndices.has(i)) {
          if (validNewCount === newImageIndex) {
            actualNewIndex = i;
            break;
          }
          validNewCount++;
        }
      }
      setRemovedNewIndices((prev) => new Set([...prev, actualNewIndex]));
    }

    setProofPreview((prev) =>
      prev.filter((_, index) => index !== displayIndex)
    );
  };

  // getDisplayImages function to prevent duplicates
  const getDisplayImages = () => {
    const existingImages = existingProofUrls
      .map((base64, index) => {
        const originalUrl = wholesalerList?.govt_id_image?.[index];
        const isPdf = originalUrl && originalUrl.toLowerCase().endsWith(".pdf");

        return {
          url: isPdf ? pdf : originalUrl || `data:image/jpeg;base64,${base64}`,
          actualUrl: originalUrl,
          isExisting: true,
          originalIndex: index,
          isPdf: isPdf,
        };
      })
      .filter((_, index) => !removedExistingIndices.has(index));

    const newImages = newProofBase64
      .map((base64, index) => {
        const previewUrl = proofPreview[existingProofUrls.length + index];
        const isPdf = previewUrl === pdf;

        return {
          url: isPdf ? pdf : `data:image/jpeg;base64,${base64}`,
          actualUrl: isPdf
            ? `data:application/pdf;base64,${base64}`
            : `data:image/jpeg;base64,${base64}`,
          isExisting: false,
          originalIndex: index,
          isPdf: isPdf,
        };
      })
      .filter((_, index) => !removedNewIndices.has(index));

    return [...existingImages, ...newImages];
  };

  const handleEditWholesalerResponse = () => {
    if (editWholesalerState?.error) {
      const errorMessage = editWholesalerState?.message;
      const errorFields = editWholesalerState?.erroressage || {};
      if (Object.keys(errorFields).length > 0) {
        Object.keys(errorFields).forEach((field) => {
          setError(field, {
            type: "manual",
            message: errorFields[field],
          });
        });
      }
      if (typeof errorMessage === "string") {
        setError("name", {
          type: "manual",
          message: errorMessage,
        });
      } else if (
        typeof errorMessage === "object" &&
        Object.keys(errorMessage).length > 0
      ) {
        Object.keys(errorMessage).forEach((field) => {
          setError(field, {
            type: "manual",
            message: errorMessage[field],
          });
        });
      } else {
        setDialogMessage("An unexpected error occurred.");
        setIsSuccess(false);
        setDialogOpen(true);
      }
    } else if (editWholesalerState?.success) {
      if (isImageOnlySave) {
        showSnackbar("Image/Proof saved successfully!", "success");
        dispatch(wholesalerViewRequest(id));
      } else {
        setDialogMessage("Wholesaler updated successfully!");
        setIsSuccess(true);
        setDialogOpen(true);
        navigate(APP_LINK.ONBOARDEDWHOLESALER);
      }

      dispatch(wholesalerResetState());
      // dispatch(wholesalerViewRequest(id));
      setIsImageOnlySave(false);
      setHasImageChanged(false);
      setRemovedExistingIndices(new Set());
      setRemovedNewIndices(new Set());
    }
  };

  useEffect(() => {
    handleEditWholesalerResponse();
  }, [editWholesalerState]);

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleImageClick = (imageUrl, isPdf = false, actualUrl = null) => {
    if (isPdf) {
      const urlToOpen = actualUrl || imageUrl;

      if (urlToOpen.startsWith("data:application/pdf;base64,")) {
        const base64Data = urlToOpen.split(",")[1];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");

        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      } else {
        window.open(urlToOpen, "_blank");
      }
    } else if (imageUrl && imageUrl !== uploadImage) {
      setSelectedImage(imageUrl);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedImage(null);
  };

  const displayImages = getDisplayImages();

  return (
    <Container>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12} lg={6} xl={5}>
            <Box>
              <Typography
                variant="h5"
                gutterBottom
                sx={styles.title}
                className="headingTable"
              >
                <h1> Edit WholeSaler Information </h1>
              </Typography>
              <Typography variant="p" gutterBottom sx={styles.photoHeading}>
                Upload Images of Wholesaler
              </Typography>
            </Box>
            <Box>
              <StyledContainer>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
                    <img
                      src={imagePreview || uploadImage}
                      alt="Profile"
                      width={80}
                      height={80}
                      style={styles.image}
                      onClick={() => handleImageClick(imagePreview, false)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={10} xl={10}>
                    <Typography
                      variant="p"
                      gutterBottom
                      sx={styles.photoContent}
                    >
                      Upload Actual Photos of Wholesaler, size less than 2MB
                    </Typography>
                    <Box sx={styles.fileButtonContainer}>
                      <CustomButton
                        variant="outlined"
                        component="label"
                        size="small"
                        buttonStyle="chooseFileButton"
                      >
                        Choose File
                        <input
                          type="file"
                          hidden
                          onChange={handleImageUpload}
                          aria-hidden="false"
                        />
                      </CustomButton>
                      <CustomButton
                        variant="contained"
                        size="small"
                        buttonStyle="fileNameButton"
                        selectedFile={Boolean(selectedFile)}
                      >
                        {imageFileName || "No File Chosen"}
                      </CustomButton>
                    </Box>
                    <Box sx={styles.saveButtonContainer}>
                      <CustomButton
                        text="Save"
                        buttonStyle="saveButton"
                        onClick={handleImage}
                        size="small"
                      >
                        Save
                      </CustomButton>
                    </Box>
                  </Grid>
                </Grid>
              </StyledContainer>
            </Box>
            <Box>
              <Typography variant="body2" gutterBottom sx={styles.note}>
                <span style={{ color: "red" }}>Note:</span> Only JPEG or PNG
                format is supported <span style={{ color: "red" }}>*</span>
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={6} xl={5}>
            <Box sx={styles.fileBoxContainer}>
              <Typography variant="p" gutterBottom sx={styles.photoHeading}>
                Upload Govt ID Proof Of Wholesaler
              </Typography>
              <StyledContainer>
                <Grid container spacing={6}>
                  {displayImages.length > 0 ? (
                    displayImages.map((imageObj, displayIndex) => {
                      const fileUrl = imageObj.url;
                      const actualUrl = imageObj.actualUrl || fileUrl;
                      const isPdf =
                        imageObj.isPdf ||
                        actualUrl.toLowerCase().includes(".pdf");

                      return (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={4}
                          lg={2}
                          xl={2}
                          key={`${
                            imageObj.isExisting ? "existing" : "new"
                          }-${displayIndex}-${imageObj.originalIndex}`}
                        >
                          <Box sx={styles.imageUploadClosePosition}>
                            <img
                              src={isPdf ? pdf : fileUrl}
                              alt={
                                isPdf
                                  ? `PDF ${displayIndex + 1}`
                                  : `Proof ${displayIndex + 1}`
                              }
                              width={80}
                              height={80}
                              style={styles.imageUploadCloseContain}
                              onClick={() => {
                                handleImageClick(actualUrl, isPdf);
                              }}
                            />
                            {/* Close (Remove) Button */}
                            <button
                              type="button"
                              onClick={() => removeProofImage(displayIndex)}
                              style={styles.imageUploadClose}
                            >
                              <CloseIcon fontSize="small" />
                            </button>
                          </Box>
                        </Grid>
                      );
                    })
                  ) : (
                    <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
                      <img
                        src={uploadImage}
                        alt="Default Upload Image"
                        width={80}
                        height={80}
                        style={styles.image}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={12} md={12} lg={10} xl={10}>
                    <Typography
                      variant="p"
                      gutterBottom
                      sx={styles.photoContent}
                    >
                      Upload Actual Photos of Proof, size less than 2MB
                    </Typography>
                    <Box sx={styles.fileButtonContainer}>
                      <CustomButton
                        text="Choose File"
                        variant="outlined"
                        component="label"
                        size="small"
                        buttonStyle="chooseFileButton"
                      >
                        Choose File
                        <input
                          type="file"
                          hidden
                          multiple
                          onChange={handleProofUpload}
                          aria-hidden="false"
                        />
                      </CustomButton>
                      <CustomButton
                        variant="contained"
                        size="small"
                        buttonStyle="fileNameButton"
                        selectedFile={Boolean(proofSelected)}
                      >
                        {proofFileName || "No File Chosen"}
                      </CustomButton>
                    </Box>
                    <Box sx={styles.saveButtonContainer}>
                      <CustomButton
                        text="Save"
                        buttonStyle="saveButton"
                        onClick={proofImage}
                        size="small"
                      >
                        Save
                      </CustomButton>
                    </Box>
                  </Grid>
                </Grid>
              </StyledContainer>
            </Box>
            <Typography variant="body2" gutterBottom sx={styles.note}>
              <span style={{ color: "red" }}>Note:</span> Only JPEG, PDF & PNG
              format is supported <span style={{ color: "red" }}>*</span>
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={8} xl={8}>
            <ReusableFormFields
              inputFields={inputFields}
              control={control}
              errors={errors}
              register={register}
              setValue={setValue}
            />
            {/* Submit Button */}
            <Box sx={styles.submitGap}>
              <Grid container spacing={2} item xs={12}>
                <Grid item xs={6}>
                  <CustomButton
                    text="Submit"
                    size="medium"
                    type="submit"
                    buttonStyle="StyledButton"
                    disabled={editWholesalerState?.inProgress}
                    loading={editWholesalerState?.inProgress}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CustomButton
                    text="Cancel"
                    variant="contained"
                    size="medium"
                    fullWidth
                    buttonStyle="style1"
                    onClick={handleCancel}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </form>
      {/* Snackbar */}
      <CommonSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />

      {/* Dialog */}
      <CommonDialog
        open={dialogOpen}
        isSuccess={isSuccess}
        onClose={handleDialogClose}
        redirectPath={APP_LINK.ADDWHOLESALER}
        messageSuccess={dialogMessage}
        messageError={dialogMessage}
      />
      {/* Popup Dialog */}
      <ImagePreviewDialog
        open={open}
        onClose={handleClose}
        selectedImage={selectedImage}
        proofSelected={proofSelected}
      />
    </Container>
  );
};

export default EditWholesaler;
